import { BreakpointObserver } from '@angular/cdk/layout';
import { DOCUMENT } from '@angular/common';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer } from '@angular/platform-browser';
import { NavigationStart, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { type Config, type DriveStep, driver, type Driver } from 'driver.js';
import { filter, merge, switchMap } from 'rxjs';
import { ProductTourStatus } from '../enums/product-tour-status';
import {
    ProductTour,
    ProductTourRecord,
    ProductTourStep,
} from '../models/product-tour';
import { PRODUCT_TOUR_STATE_STORE } from '../tokens/product-tour-state-store.token';
import { sanitizeTrustedHtml } from '../utils/sanitize-trusted-html';

interface ActiveTour {
    tour: ProductTour;
    instance: Driver;
    reachedLastStep: boolean;
    /** Element focused when the tour started - focus returns here when the tour ends. */
    returnFocus: Element | null;
}

/**
 * The only place aware of the tour library (driver.js) - replacing the library means
 * re-implementing this service while keeping its public API.
 */
@Injectable({ providedIn: 'root' })
export class ProductTourService {
    private readonly breakpointObserver = inject(BreakpointObserver);
    private readonly destroyRef = inject(DestroyRef);
    private readonly document = inject(DOCUMENT);
    private readonly router = inject(Router);
    private readonly sanitizer = inject(DomSanitizer);
    private readonly stateStore = inject(PRODUCT_TOUR_STATE_STORE);
    private readonly translateService = inject(TranslateService);

    private activeTour?: ActiveTour;

    constructor() {
        // Tour copy is translated on start and anchors belong to the current page
        merge(
            this.translateService.onLangChange,
            this.router.events.pipe(
                filter((event) => event instanceof NavigationStart)
            )
        )
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.stop());
    }

    /**
     * Starts the tour once per user and tour version. Skipped while another tour is running
     * and whenever the tour state cannot be read or persisted.
     */
    autoStart(tour: ProductTour): void {
        if (this.activeTour) {
            return;
        }

        this.stateStore
            .get(tour.id)
            .pipe(
                filter((record) => !record || record.version < tour.version),
                // Persisted before showing, so closing the tab mid-tour does not re-trigger it
                switchMap(() =>
                    this.stateStore.set(
                        tour.id,
                        this.createRecord(tour, ProductTourStatus.SEEN)
                    )
                ),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: () => {
                    // Another tour may have started while the state store was read
                    if (!this.activeTour) {
                        this.start(tour);
                    }
                },
                // State unavailable (no user, blocked storage) - never auto-start
                error: () => undefined,
            });
    }

    start(tour: ProductTour): void {
        this.stop();

        const activeTour: ActiveTour = {
            tour,
            reachedLastStep: false,
            returnFocus: this.document.activeElement,
            instance: driver({
                ...this.getConfig(),
                steps: tour.steps.map((step) => this.toDriveStep(step)),
                onHighlightStarted: (_element, _step, { driver: instance }) => {
                    activeTour.reachedLastStep ||= instance.isLastStep();
                },
                // Called on every library-initiated end (close button, Esc, overlay click, Done,
                // no step left) and leaves the destroy to us. Unlike `onDestroyed`, it also fires
                // when no step has been highlighted yet.
                onDestroyStarted: (_element, _step, { driver: instance }) => {
                    instance.destroy();
                    this.finish(activeTour);
                },
            }),
        };

        this.activeTour = activeTour;
        tour.onStarted?.();
        activeTour.instance.drive();
    }

    /** Pass the tour to stop only that tour, e.g. from a host's teardown. */
    stop(tour?: ProductTour): void {
        const activeTour = this.activeTour;

        if (!activeTour || (tour && activeTour.tour.id !== tour.id)) {
            return;
        }

        // destroy() does not invoke onDestroyStarted
        activeTour.instance.destroy();
        this.finish(activeTour);
    }

    private finish(activeTour: ActiveTour): void {
        // Already finished, or replaced by a newer tour
        if (this.activeTour !== activeTour) {
            return;
        }

        this.activeTour = undefined;

        const status = activeTour.reachedLastStep
            ? ProductTourStatus.COMPLETED
            : ProductTourStatus.DISMISSED;

        this.stateStore
            .set(activeTour.tour.id, this.createRecord(activeTour.tour, status))
            .subscribe({ error: () => undefined });

        activeTour.tour.onDestroyed?.();
        this.restoreFocus(activeTour.returnFocus);
    }

    /**
     * driver.js refocuses the element that was active when the last shown step started - from
     * step 2 on, that is the previous popover's button, already removed, so focus falls to body.
     * Only acts when focus was lost, so a focus restored by someone else (e.g. MatDialog) wins.
     */
    private restoreFocus(element: Element | null): void {
        const focused = this.document.activeElement;
        const focusLost = !focused || focused === this.document.body;

        if (
            focusLost &&
            element instanceof HTMLElement &&
            element.isConnected
        ) {
            element.focus();
        }
    }

    private getConfig(): Config {
        const closeLabel = this.translateService.instant(
            'shared.productTour.buttons.close'
        );
        // driver.js ignores the OS setting itself - read it on every start
        const allowMotion = !this.breakpointObserver.isMatched(
            '(prefers-reduced-motion: reduce)'
        );

        return {
            // false = no fade-in and no highlight moving between steps
            animate: allowMotion,
            disableActiveInteraction: true,
            doneBtnText: this.translate('shared.productTour.buttons.done'),
            nextBtnText: this.translate('shared.productTour.buttons.next'),
            popoverClass: 'lemunzo-tour',
            prevBtnText: this.translate('shared.productTour.buttons.prev'),
            // Keeps the library's own placeholders, which it fills in per step
            progressText: this.translate('shared.productTour.progress', {
                current: '{{current}}',
                total: '{{total}}',
            }),
            showProgress: true,
            skipMissingElement: true,
            smoothScroll: allowMotion,
            waitForElement: 1000,
            // setAttribute does not parse HTML - no sanitizing needed
            onPopoverRender: (popover) =>
                popover.closeButton.setAttribute('aria-label', closeLabel),
        };
    }

    /** Title and description are rendered as sanitized HTML - see `translate()`. */
    private toDriveStep(step: ProductTourStep): DriveStep {
        return {
            element: step.selector,
            popover: {
                title: this.translate(step.titleKey),
                description: this.translate(step.descriptionKey),
                side: step.side,
                align: step.align,
            },
        };
    }

    /**
     * Translation for text driver.js renders via innerHTML. Sanitized as a precaution against
     * potential XSS, the same way Angular treats an `[innerHTML]` binding - keeps a future
     * interpolated or backend-sourced value from injecting scripts or event handlers.
     */
    private translate(key: string, params?: Record<string, string>): string {
        return sanitizeTrustedHtml(
            this.sanitizer,
            this.translateService.instant(key, params)
        );
    }

    private createRecord(
        tour: ProductTour,
        status: ProductTourStatus
    ): ProductTourRecord {
        return {
            version: tour.version,
            status,
            updatedAt: new Date().toISOString(),
        };
    }
}
